import { getShiftDisplayLabel } from "@/types";
import { DEFAULT_AVAILABILITY_SHIFTS } from "./availabilityShifts";

export const ROUTE_REASON = "Tienes un viaje asignado este día";
export const EXISTING_REASON =
  "Ya tienes disponibilidades para este día. Usa la edición para modificarlas.";

const dayOf = (iso: string) => iso.split("T")[0];

/** fecha (YYYY-MM-DD) → operaciones que ya tienen filas ese día. */
export const buildExistingByDate = (
  avs: { date: string; svc: { operationId: number } }[]
): Map<string, Set<number>> => {
  const out = new Map<string, Set<number>>();
  for (const a of avs) {
    const key = dayOf(a.date);
    if (!out.has(key)) out.set(key, new Set());
    out.get(key)!.add(a.svc.operationId);
  }
  return out;
};

/** fecha → turnos con ruta. Un turno desconocido ("N/A") bloquea todos, como hoy. */
export const buildRouteShiftsByDate = (
  shipments: { date: string; shift: string }[]
): Map<string, Set<string>> => {
  const out = new Map<string, Set<string>>();
  for (const s of shipments) {
    if (!s.date) continue;
    const key = dayOf(s.date);
    if (!out.has(key)) out.set(key, new Set());
    const set = out.get(key)!;
    if (DEFAULT_AVAILABILITY_SHIFTS.includes(s.shift)) set.add(s.shift);
    else DEFAULT_AVAILABILITY_SHIFTS.forEach((sh) => set.add(sh));
  }
  return out;
};

export type DayBlock = {
  disabled: boolean;
  reason?: string;
  hint?: string;
  /** Turnos a saltear en el fan-out para este día (ruta ya asignada). */
  skipShifts: string[];
};

export type DayBlockInput = {
  date: string;
  /** Operaciones efectivas (las marcadas; con una sola operación, esa). */
  operationIds: number[];
  operationNames: Map<number, string>;
  multiOp: boolean;
  /** Turnos marcados; si no hay ninguno, los ofrecidos. */
  shiftsToConsider: string[];
  existingByDate: Map<string, Set<number>>;
  routeShiftsByDate: Map<string, Set<string>>;
  /**
   * Nombre de un turno para las operaciones dadas (el componente lo resuelve
   * con useShiftDisplay). Sin él se usa el default ("Mañana"/"Tarde").
   */
  shiftLabel?: (code: string, operationIds: number[]) => string;
};

/**
 * Reglas en orden: (1) ruta en todos los turnos → bloqueado; (2) disponibilidad
 * ya cargada para una operación marcada → bloqueado; (3) ruta en parte de los
 * turnos → habilitado con nota y esos turnos se saltean; (4) libre.
 * Con una sola operación y sin rutas parciales reproduce el bloqueo de siempre.
 */
export const getDayBlock = (input: DayBlockInput): DayBlock => {
  const { date, operationIds, operationNames, multiOp, shiftsToConsider, existingByDate, routeShiftsByDate } = input;
  const labelOf = (code: string) =>
    input.shiftLabel ? input.shiftLabel(code, operationIds) : getShiftDisplayLabel(code);
  const routed = routeShiftsByDate.get(date) ?? new Set<string>();
  const routedConsidered = shiftsToConsider.filter((s) => routed.has(s));

  if (shiftsToConsider.length > 0 && routedConsidered.length === shiftsToConsider.length) {
    return { disabled: true, reason: ROUTE_REASON, skipShifts: [] };
  }

  const existing = existingByDate.get(date);
  const blockedOp = existing ? operationIds.find((id) => existing.has(id)) : undefined;
  if (blockedOp !== undefined) {
    const name = operationNames.get(blockedOp) ?? `Operación ${blockedOp}`;
    const reason = multiOp
      ? `Ya tienes disponibilidades de ${name} para este día. Usa la edición para modificarlas.`
      : EXISTING_REASON;
    return { disabled: true, reason, skipShifts: [] };
  }

  if (routedConsidered.length > 0) {
    const free = shiftsToConsider.filter((s) => !routed.has(s));
    const hint = `Tienes viaje en el turno ${routedConsidered.map(labelOf).join(" y ")}: se cargará solo ${free.map(labelOf).join(" y ")}`;
    return { disabled: false, hint, skipShifts: routedConsidered };
  }

  return { disabled: false, skipShifts: [] };
};
